// src/components/ScheduledDeletionScreen.tsx
// Shown when user.deletedAt is set — replaces the normal app experience.
// The user can cancel here during the 7-day grace period.

import { useState } from 'react';

const BRAND_CORAL  = '#E05A3A';
const BRAND_CREAM  = '#F0DFC8';
const BRAND_INK    = '#1A1A1A';
const PAGE_BG      = '#FDF6EE';

interface Props {
  deletedAt: string;              // ISO timestamp from User.deletedAt
  onCancelSuccess: () => void;    // called when cancelAccountDeletion succeeds
  onCancelDeletion: () => Promise<void>;  // calls the REST endpoint
}

function addDays(iso: string, days: number): Date {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

export default function ScheduledDeletionScreen({
  deletedAt,
  onCancelSuccess,
  onCancelDeletion,
}: Props) {
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const deleteDate = addDays(deletedAt, 7);
  const dateLabel  = formatDate(deleteDate);

  async function handleCancel() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await onCancelDeletion();
      onCancelSuccess();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div style={{
      minHeight: '100dvh',
      background: PAGE_BG,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      fontFamily: "'Nunito', sans-serif",
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 20,
        border: `3px solid ${BRAND_INK}`,
        boxShadow: `6px 6px 0px ${BRAND_INK}`,
        padding: '36px 28px',
        maxWidth: 420,
        width: '100%',
        textAlign: 'center',
      }}>
        {/* Icon */}
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: BRAND_CREAM,
          border: `3px solid ${BRAND_INK}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 28,
        }}>
          🗓️
        </div>

        <h1 style={{
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 24,
          fontWeight: 600,
          color: BRAND_INK,
          marginBottom: 12,
        }}>
          Account scheduled for deletion
        </h1>

        <p style={{
          fontSize: 15,
          color: '#444',
          lineHeight: 1.6,
          marginBottom: 8,
        }}>
          Your account is locked. All data will be permanently removed from Crushhh on:
        </p>

        <p style={{
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 18,
          fontWeight: 600,
          color: BRAND_CORAL,
          marginBottom: 20,
        }}>
          {dateLabel}
        </p>

        <p style={{
          fontSize: 13,
          color: '#666',
          lineHeight: 1.6,
          marginBottom: 28,
        }}>
          Changed your mind? Cancel now to restore your account before the deadline.
          Payment records are kept as required by law with personal details stripped.
        </p>

        {error && (
          <p style={{
            fontSize: 13,
            color: BRAND_CORAL,
            marginBottom: 12,
          }}>
            {error}
          </p>
        )}

        <button
          onClick={handleCancel}
          disabled={busy}
          style={{
            width: '100%',
            padding: '14px 0',
            borderRadius: 100,
            border: 'none',
            background: busy ? '#ccc' : BRAND_CORAL,
            color: '#fff',
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 18,
            fontWeight: 600,
            cursor: busy ? 'default' : 'pointer',
            boxShadow: busy ? 'none' : `3px 3px 0px ${BRAND_INK}`,
            transition: 'background 0.15s',
          }}
        >
          {busy ? 'Restoring…' : 'Cancel deletion — restore my account'}
        </button>
      </div>
    </div>
  );
}
